import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, IntegrationBadge, PageHeader, Stat } from "@/components/app/StatusBadge";
import { api, formatApiError } from "@/lib/api";
import { useApp } from "@/lib/i18n";
import { ContactMessages } from "@/components/admin/ContactMessages";
import { TestimonialsAdmin } from "@/components/admin/TestimonialsAdmin";
import { ErrorReports } from "@/components/admin/ErrorReports";
import { AdminNotifications } from "@/components/admin/AdminNotifications";
import { fmtDate, subLabel } from "@/components/billing/CycleInfo";

const run = async (fn, ok) => { try { await fn(); ok && toast.success(ok); return true; } catch (e) { toast.error(formatApiError(e)); return false; } };

const Users = () => {
  const [users, setUsers] = useState([]);
  const load = () => api.get("/admin/users").then((r) => setUsers(r.data));
  useEffect(() => { load(); }, []);
  return (
    <Card className="divide-y divide-slate-100" data-testid="admin-users">
      {users.map((u) => (
        <div key={u.id} className="flex flex-wrap items-center justify-between gap-2 p-4 text-sm">
          <span><strong>{u.name}</strong> <span className="text-slate-500">{u.email}</span> {u.is_super_admin && <span className="ml-1 rounded bg-violet-100 px-1.5 text-xs text-violet-800">super admin</span>}</span>
          {!u.is_super_admin && (
            <Button size="sm" variant="outline" className={u.status === "suspended" ? "text-emerald-700" : "text-red-600"} data-testid={`admin-toggle-user-${u.id}`}
              onClick={() => run(() => api.patch(`/admin/users/${u.id}`, { status: u.status === "suspended" ? "active" : "suspended" })).then(load)}>
              {u.status === "suspended" ? "Aktifkan" : "Gantung"}
            </Button>
          )}
        </div>
      ))}
    </Card>
  );
};

const Workspaces = ({ plans }) => {
  const [items, setItems] = useState([]);
  const load = () => api.get("/admin/workspaces").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);
  return (
    <Card className="divide-y divide-slate-100" data-testid="admin-workspaces">
      {items.map((w) => (
        <div key={w.id} className="flex flex-wrap items-center justify-between gap-2 p-4 text-sm">
          <span className="min-w-0">
            <strong>{w.name}</strong> <span className="text-slate-500">{w.type} · {w.owner_email}</span>
            <span className="mt-1 block text-xs text-slate-500" data-testid={`admin-ws-cycle-${w.id}`}>
              {w.plan === "free" ? "Pelan Percuma" : `Kitaran kuota: ${fmtDate(w.cycle.start, "ms")} – ${fmtDate(w.cycle.end, "ms")} · mula langgan ${fmtDate(w.billing_anchor, "ms")}`}
              {w.plan !== "free" && subLabel(w.subscription, (ms) => ms, "ms") && ` · ${subLabel(w.subscription, (ms) => ms, "ms")}`}
            </span>
          </span>
          <Select value={w.plan} onValueChange={(v) => run(() => api.patch(`/admin/workspaces/${w.id}`, { plan: v }), "Pelan dikemas kini").then(load)}>
            <SelectTrigger className="h-8 w-28 rounded-lg" data-testid={`admin-plan-${w.id}`}><SelectValue /></SelectTrigger>
            <SelectContent>{plans.map((p) => <SelectItem key={p.key} value={p.key}>{p.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      ))}
    </Card>
  );
};

const Plans = ({ plans, reload }) => (
  <div className="grid gap-4 md:grid-cols-3" data-testid="admin-plans">
    {plans.map((p) => <PlanEditor key={p.key} plan={p} reload={reload} />)}
  </div>
);

const PlanEditor = ({ plan, reload }) => {
  const [p, setP] = useState(plan);
  const fields = [["price_myr", "Harga (MYR)"], ["trial_days", "Hari percubaan"], ["minutes_quota", "Kuota minit"], ["ai_credits", "Dokumen/bulan"], ["max_members", "Maks ahli"], ["price_yearly_myr", "Harga tahunan"], ["max_duration_min", "Maks minit/rakaman"]];
  return (
    <Card className="space-y-3 p-4">
      <Input value={p.name} onChange={(e) => setP({ ...p, name: e.target.value })} className="font-semibold" />
      {fields.map(([k, l]) => (
        <label key={k} className="flex items-center justify-between gap-2 text-xs">{l}<Input type="number" value={p[k]} onChange={(e) => setP({ ...p, [k]: Number(e.target.value) })} className="h-8 w-28" data-testid={`plan-${plan.key}-${k}`} /></label>
      ))}
      <Button size="sm" className="w-full bg-violet-600 hover:bg-violet-700" data-testid={`save-plan-${plan.key}`} onClick={() => run(() => api.put(`/admin/plans/${plan.key}`, p), "Pelan disimpan").then(reload)}>Simpan</Button>
    </Card>
  );
};

const Templates = () => {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/admin/templates").then((r) => setItems(r.data)); }, []);
  const update = (i, k, v) => setItems((l) => l.map((x, j) => (j === i ? { ...x, [k]: v } : x)));
  return (
    <div className="space-y-4" data-testid="admin-templates">
      {items.map((t, i) => (
        <Card key={t.key} className="space-y-2 p-4">
          <div className="grid gap-2 sm:grid-cols-2"><Input value={t.name_ms} onChange={(e) => update(i, "name_ms", e.target.value)} /><Input value={t.name_en} onChange={(e) => update(i, "name_en", e.target.value)} /></div>
          <Textarea value={t.instructions} onChange={(e) => update(i, "instructions", e.target.value)} className="min-h-[90px] text-xs" />
          <Button size="sm" onClick={() => run(() => api.put(`/admin/templates/${t.key}`, t), "Template disimpan")} data-testid={`save-template-${t.key}`}>Simpan</Button>
        </Card>
      ))}
    </div>
  );
};

const Branding = () => {
  const { brand } = useApp();
  const [b, setB] = useState(brand);
  return (
    <Card className="grid gap-3 p-5 sm:grid-cols-2" data-testid="admin-branding">
      {Object.keys(brand).map((k) => (
        <label key={k} className="space-y-1 text-xs font-semibold text-slate-600">{k}<Input value={b[k]} onChange={(e) => setB({ ...b, [k]: e.target.value })} data-testid={`brand-${k}`} /></label>
      ))}
      <Button className="bg-violet-600 hover:bg-violet-700 sm:col-span-2" onClick={() => run(() => api.put("/admin/branding", b), "Branding disimpan — muat semula untuk melihat").then(() => {})} data-testid="save-branding-btn">Simpan branding</Button>
    </Card>
  );
};

const Jobs = () => {
  const [items, setItems] = useState([]);
  const load = () => api.get("/admin/jobs", { params: { status: "failed" } }).then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);
  return (
    <Card className="divide-y divide-slate-100" data-testid="admin-jobs">
      {items.length === 0 && <p className="p-4 text-sm text-slate-500">Tiada job gagal.</p>}
      {items.map((j) => (
        <div key={j.id} className="flex flex-wrap items-center justify-between gap-2 p-4 text-sm">
          <span className="min-w-0"><span className="font-mono text-xs">{j.id.slice(0, 8)}</span> · {j.kind} · <span className="text-red-600">{j.error}</span></span>
          <Button size="sm" variant="outline" onClick={() => run(() => api.post(`/admin/jobs/${j.id}/retry`), "Job dimasukkan semula").then(load)} data-testid={`admin-retry-${j.id}`}>Cuba semula</Button>
        </div>
      ))}
    </Card>
  );
};

const Audit = () => {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/admin/audit").then((r) => setItems(r.data)); }, []);
  return (
    <Card className="max-h-[600px] divide-y divide-slate-100 overflow-y-auto text-xs" data-testid="admin-audit">
      {items.map((a) => <div key={a.id} className="flex justify-between gap-3 p-3"><span className="font-mono">{a.action}</span><span className="truncate text-slate-500">{a.target}</span><span className="shrink-0 text-slate-400">{new Date(a.created_at).toLocaleString("ms-MY")}</span></div>)}
    </Card>
  );
};

const IntegrationsCfg = () => {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/admin/integrations").then((r) => setItems(r.data)); }, []);
  return (
    <Card className="divide-y divide-slate-100" data-testid="admin-integrations">
      <p className="p-4 text-xs text-slate-500">Kunci API & rahsia OAuth ditetapkan melalui environment variables pelayan (lihat README). Status di bawah dikesan secara automatik.</p>
      {items.map((i) => <div key={i.key} className="flex items-center justify-between p-4 text-sm">{i.name}<IntegrationBadge status={i.status} /></div>)}
    </Card>
  );
};

export default function Admin() {
  const [stats, setStats] = useState(null);
  const [plans, setPlans] = useState([]);
  const loadPlans = () => api.get("/public/config").then((r) => setPlans(r.data.plans));
  useEffect(() => { api.get("/admin/stats").then((r) => setStats(r.data)); loadPlans(); }, []);
  const tabs = [["users", "Pengguna", <Users />], ["contact", "Mesej hubungan", <ContactMessages />], ["workspaces", "Workspace & pelan", <Workspaces plans={plans} />], ["plans", "Konfigurasi pelan", <Plans plans={plans} reload={loadPlans} />], ["templates", "Template", <Templates />], ["branding", "Branding", <Branding />], ["testimonials", "Testimoni", <TestimonialsAdmin />], ["jobs", "Job gagal", <Jobs />], ["errors", "Ralat", <ErrorReports />], ["notifications", "Notifikasi emel", <AdminNotifications />], ["integrations", "Integrasi", <IntegrationsCfg />], ["audit", "Audit log", <Audit />]];
  return (
    <div className="mx-auto max-w-6xl px-4 py-8 sm:px-8" data-testid="admin-page">
      <PageHeader title="Panel Pentadbir" desc="Pentadbir tidak boleh membaca kandungan mesyuarat pengguna melalui panel ini — hanya metadata." />
      {stats && (
        <div className="mt-6 grid grid-cols-2 gap-4 lg:grid-cols-5">
          {[["users", "Pengguna"], ["workspaces", "Workspace"], ["meetings", "Mesyuarat"], ["active_jobs", "Job aktif"], ["failed_jobs", "Job gagal"]].map(([k, l]) => <Stat key={k} id={`admin-${k}`} label={l} value={stats[k]} />)}
        </div>
      )}
      <Tabs defaultValue="users" className="mt-6">
        <TabsList className="flex h-auto flex-wrap justify-start gap-1 rounded-2xl bg-slate-100 p-1">
          {tabs.map(([k, l]) => <TabsTrigger key={k} value={k} className="rounded-xl" data-testid={`admin-tab-${k}`}>{l}</TabsTrigger>)}
        </TabsList>
        {tabs.map(([k, , el]) => <TabsContent key={k} value={k} className="mt-5">{el}</TabsContent>)}
      </Tabs>
    </div>
  );
}
